/**
 * ConfluenceSection
 *
 * Renders the Confluence Factors section inside the TradeDrawer.
 * Displays a 2-column layout matching the visual pattern of
 * LinkedConfluencesDualList (Settings → Strategies): Card columns with
 * ThumbsUp/ThumbsDown icon headers, one per polarity.
 *
 * Each item is a checkbox — checked = present/confirmed.
 * "X/Y present" summary badge above the columns.
 *
 * When there are more than 10 confluence items (any polarity combined), a
 * search/filter Input appears above the dual-column grid on desktop. The
 * column headers reflect the filtered count.
 *
 * @see LinkedConfluencesDualList — same visual pattern (settings, management model)
 * @see ConfluenceSnapshot — confluence data shape used in the drawer
 */

// 1. External dependencies
import React, { useEffect, useMemo, useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';

// 2. Internal absolute imports
import { Card, CardBody, CardHeader, Checkbox, Input } from '@enfomentis/ui';
import { useIsDesktop } from '@/hooks/useIsDesktop';

// 3. Type imports
import type { ConfluenceSnapshot } from '@/types/shared/StrategySnapshots';

// ===== CONSTANTS =====

/** Minimum total confluence count that triggers the search bar (desktop only) */
const SEARCH_THRESHOLD = 10;

// ===== TYPES =====

interface ConfluenceSectionProps {
  confluences: ConfluenceSnapshot[];
  onChange: (updated: ConfluenceSnapshot[]) => void;
}

// ===== COMPONENT =====

export const ConfluenceSection: React.FC<ConfluenceSectionProps> = ({ confluences, onChange }) => {
  const isDesktop = useIsDesktop();
  const [searchQuery, setSearchQuery] = useState('');

  const showSearch = isDesktop && confluences.length > SEARCH_THRESHOLD;

  // Clear stale filter when search bar is hidden (mobile resize / low count)
  useEffect(() => {
    if (!showSearch) setSearchQuery('');
  }, [showSearch]);

  const { positives, negatives, presentCount } = useMemo(() => {
    const lowerQuery = searchQuery.trim().toLowerCase();
    const filtered = lowerQuery
      ? confluences.filter((c) => c.name.toLowerCase().includes(lowerQuery))
      : confluences;

    return {
      positives: filtered.filter((c) => c.polarity === 'positive'),
      negatives: filtered.filter((c) => c.polarity === 'negative'),
      presentCount: confluences.filter((c) => c.checked).length,
    };
  }, [confluences, searchQuery]);

  const toggle = (confluenceId: string): void => {
    onChange(confluences.map((c) =>
      c.confluenceId === confluenceId ? { ...c, checked: !c.checked } : c
    ));
  };

  if (confluences.length === 0) {
    return (
      <p className="text-xs text-text-tertiary italic">Select a playbook to populate confluence factors.</p>
    );
  }

  const renderColumn = (
    polarity: 'positive' | 'negative',
    items: ConfluenceSnapshot[]
  ): React.ReactNode => {
    const isPositive = polarity === 'positive';
    const Icon = isPositive ? ThumbsUp : ThumbsDown;
    const iconClass = isPositive ? 'text-success' : 'text-warning';
    const label = isPositive ? 'Positive' : 'Negative';

    return (
      <Card shadow="sm" radius="sm" className="min-w-0 border border-default-200">
        <CardHeader className="flex items-center gap-2 bg-default-50 py-2 px-3">
          <Icon size={14} className={iconClass} />
          <span className="text-xs font-medium text-text-primary">
            {label} ({items.length})
          </span>
        </CardHeader>
        <CardBody className="py-2 px-3">
          {items.length > 0 ? (
            <div className={`flex flex-col gap-1${items.length > 10 ? ' max-h-64 overflow-y-auto pr-1' : ''}`}>
              {items.map((c) => (
                <Checkbox
                  key={c.confluenceId}
                  isSelected={c.checked}
                  onValueChange={() => toggle(c.confluenceId)}
                  size="sm"
                  classNames={{ label: 'text-xs text-text-primary' }}
                >
                  {c.name}
                </Checkbox>
              ))}
            </div>
          ) : (
            <p className="text-xs text-text-tertiary py-1">
              {searchQuery.trim() ? 'No matches' : 'None'}
            </p>
          )}
        </CardBody>
      </Card>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      {/* X/Y present badge */}
      <span className="text-xs text-text-secondary">
        <span className={`font-semibold ${presentCount > 0 ? 'text-text-primary' : 'text-text-tertiary'}`}>
          {presentCount}/{confluences.length}
        </span>
        {' '}present
      </span>

      {/* Search bar — desktop only, shown when confluence count exceeds threshold */}
      {showSearch && (
        <Input
          size="sm"
          variant="bordered"
          placeholder="Filter confluences…"
          value={searchQuery}
          onValueChange={setSearchQuery}
          isClearable
          onClear={() => setSearchQuery('')}
          classNames={{ inputWrapper: 'h-7' }}
        />
      )}

      {/* Dual columns — matches LinkedConfluencesDualList visual pattern */}
      <div className="grid grid-cols-2 gap-2">
        {renderColumn('positive', positives)}
        {renderColumn('negative', negatives)}
      </div>
    </div>
  );
};
